import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { UserProvider, useUser } from './contexts/UserContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Authentication components
import Login from './features/auth/Login';
import Register from './features/auth/Register';
import AutoProvision from './features/auth/AutoProvision';
import ForgotPassword from './features/auth/ForgotPassword';

// Common components
import LoadingScreen from './components/common/LoadingScreen';
import FeatureRoute from './components/FeatureRoute';
import ModuleRoute from './components/ModuleRoute';

// Module system
import { loadModules, getModuleRegistry } from './modules';

// Lazy load main application components
const Dashboard = React.lazy(() => import('./modules/presentations/Dashboard'));
const Referrals = React.lazy(() => import('./features/referrals/Referrals'));

const Feedback = React.lazy(() => import('./features/feedback/Feedback'));

const MainLayout = React.lazy(() => import('./features/layout/MainLayout'));

// Lazy load admin components
const UserManagement = React.lazy(() => import('./features/users/UserManagement'));
const AdminMessages = React.lazy(() => import('./features/settings/AdminMessages'));
const FeedbackResponse = React.lazy(() => import('./features/feedback/FeedbackResponse'));
const GlobalSettings = React.lazy(() => import('./features/settings/GlobalSettings'));
const StripeSettings = React.lazy(() => import('./features/settings/StripeSettings'));
const TenantSettings = React.lazy(() => import('./features/tenants/TenantSettings'));
const UsageStatistics = React.lazy(() => import('./features/tenants/UsageStatistics'));
const TenantUsageActivityPage = React.lazy(() => import('./features/tenants/TenantUsageActivityPage'));

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
    const { user, loading, isGlobalAdmin } = useUser();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Global admins can have tenantId: null, so we don't redirect them to auto-provision
    if (!user.tenantId && !isGlobalAdmin()) {
        return <Navigate to="/auto-provision" replace />;
    }

    return children;
};

// Public route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }: { children: React.ReactElement }) => {
    const { user, loading, isGlobalAdmin } = useUser();

    if (loading) {
        return <LoadingScreen />;
    }

    if (user) {
        // Global admins can have tenantId: null, so we don't redirect them to auto-provision
        if (!user.tenantId && !isGlobalAdmin()) {
            return <Navigate to="/auto-provision" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Auto-provision route (only accessible to authenticated users without tenant)
const AutoProvisionRoute = ({ children }: { children: React.ReactElement }) => {
    const { user, loading } = useUser();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (user.tenantId) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Global Admin route (only accessible to Global Admin)
const GlobalAdminRoute = ({ children }: { children: React.ReactElement }) => {
    const { user, loading, isGlobalAdmin } = useUser();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Global admins can have tenantId: null, so we don't redirect them to auto-provision
    if (!isGlobalAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Lazy loading wrapper component
const LazyComponent = ({ children }: { children: React.ReactNode }) => (
    <Suspense fallback={<LoadingScreen message="Loading component..." />}>
        {children}
    </Suspense>
);

// App Router Component
const AppRouter = () => {
    const { user, userProfile } = useUser();
    const moduleRegistry = getModuleRegistry();

    // Load modules on first render
    useEffect(() => {
        loadModules();
    }, []);

    // Get module routes
    const moduleRoutes = user && userProfile ? moduleRegistry.getRoutes(user, userProfile) : [];


    return (
        <Router>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } />
                <Route path="/register" element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                } />
                <Route path="/forgot-password" element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                } />

                {/* Auto-provision route */}
                <Route path="/auto-provision" element={
                    <AutoProvisionRoute>
                        <AutoProvision />
                    </AutoProvisionRoute>
                } />

                {/* Protected routes with lazy loading */}
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <LazyComponent>
                            <MainLayout>
                                <Dashboard />
                            </MainLayout>
                        </LazyComponent>
                    </ProtectedRoute>
                } />
                <Route path="/referrals" element={
                    <FeatureRoute feature="referrals">
                        <ProtectedRoute>
                            <LazyComponent>
                                <MainLayout>
                                    <Referrals />
                                </MainLayout>
                            </LazyComponent>
                        </ProtectedRoute>
                    </FeatureRoute>
                } />

                {/* Module routes with lazy loading */}
                {moduleRoutes.map((route: any) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <ModuleRoute module={route.module}>
                                <ProtectedRoute>
                                    <LazyComponent>
                                        <MainLayout>
                                            {route.element}
                                        </MainLayout>
                                    </LazyComponent>
                                </ProtectedRoute>
                            </ModuleRoute>
                        }
                    />
                ))}


                <Route path="/feedback" element={
                    <FeatureRoute feature="feedback">
                        <ProtectedRoute>
                            <MainLayout>
                                <Feedback />
                            </MainLayout>
                        </ProtectedRoute>
                    </FeatureRoute>
                } />


                {/* Admin routes */}
                <Route path="/admin/users" element={
                    <ProtectedRoute>
                        <LazyComponent>
                            <MainLayout>
                                <UserManagement />
                            </MainLayout>
                        </LazyComponent>
                    </ProtectedRoute>
                } />
                <Route path="/admin/messages" element={
                    <GlobalAdminRoute>
                        <MainLayout>
                            <AdminMessages />
                        </MainLayout>
                    </GlobalAdminRoute>
                } />
                <Route path="/admin/feedback" element={
                    <FeatureRoute feature="feedback">
                        <ProtectedRoute>
                            <MainLayout>
                                <FeedbackResponse />
                            </MainLayout>
                        </ProtectedRoute>
                    </FeatureRoute>
                } />
                <Route path="/admin/settings" element={
                    <GlobalAdminRoute>
                        <MainLayout>
                            <GlobalSettings />
                        </MainLayout>
                    </GlobalAdminRoute>
                } />
                <Route path="/admin/stripe-settings" element={
                    <GlobalAdminRoute>
                        <MainLayout>
                            <StripeSettings />
                        </MainLayout>
                    </GlobalAdminRoute>
                } />
                <Route path="/admin/usage-statistics" element={
                    <GlobalAdminRoute>
                        <MainLayout>
                            <UsageStatistics />
                        </MainLayout>
                    </GlobalAdminRoute>
                } />
                <Route path="/admin/tenant-settings" element={
                    <ProtectedRoute>
                        <MainLayout>
                            <TenantSettings />
                        </MainLayout>
                    </ProtectedRoute>
                } />
                <Route path="/admin/usage-activity" element={
                    <ProtectedRoute>
                        <MainLayout>
                            <TenantUsageActivityPage />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                {/* Default route */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* Catch all route - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </Router>
    );
};

// Main App component
function App() {
    return (
        <UserProvider>
            <ThemeProvider>
                <AppWithTheme />
            </ThemeProvider>
        </UserProvider>
    );
}

// App component with theme
function AppWithTheme() {
    const { theme } = useTheme();

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppRouter />
            </LocalizationProvider>
        </MuiThemeProvider>
    );
}

export default App;
